import { MondayApiResponse } from '@/types/common';
import {
  Board,
  Column,
  ColumnType,
  ColumnValue,
  Item,
} from '@mondaydotcomorg/api-types';
import { API, axios, getSessionToken } from '../utils/http';

export type BoardNameAndColumnsResponse = MondayApiResponse<{
  boards: Array<
    Pick<Board, 'id' | 'name'> & {
      columns: Pick<Column, 'id' | 'title' | 'type' | 'settings_str'>[];
    }
  >;
}>;

export type ItemAndBoardDetailsResponse = MondayApiResponse<{
  items: Array<
    Pick<Item, 'id' | 'name' | 'state'> & {
      board: Pick<Board, 'id' | 'name'>;
    }
  >;
}>;

export type SubItemsResponse = MondayApiResponse<{
  items: Array<
    Pick<Item, 'id' | 'name'> & {
      board: {
        columns: Pick<Column, 'id' | 'type' | 'settings_str'>[];
      };
      subitems: Array<
        Pick<Item, 'id' | 'name'> & {
          column_values: ColumnValue[];
        }
      >;
    }
  >;
}>;

export type EmailColumnValueResponse = MondayApiResponse<{
  items: Array<{
    id: string;
    column_values: ColumnValue[];
  }>;
}>;

export type UpdateStatusColumnResponse = MondayApiResponse<{
  change_multiple_column_values: {
    id: string;
  };
}>;

export type CreateColumnResponse = MondayApiResponse<{
  create_column: Pick<
    Column,
    'id' | 'title' | 'description' | 'type' | 'settings_str'
  >;
}>;

export type ColumnValuesResponse = MondayApiResponse<{
  items: Array<{
    id: string;
    name: string;
    board: {
      id: string;
      name: string;
      columns: Array<{
        id: string;
        title: string;
        type: string;
        settings_str: string;
      }>;
    };
    column_values: Array<{
      id: string;
      text: string;
      type: ColumnType;
      column: {
        title: string;
      };
      email?: string;
      display_value?: string;
      mirrored_items?: Array<{
        linked_board_id: string;
        linked_item: { id: string };
      }>;
      persons_and_teams?: Array<{
        id: string;
        kind: string;
      }>;
    }>;
  }>;
}>;

export type ItemDetails = NonNullable<ColumnValuesResponse['data']>['items'][0];

export const updateBackOfficeInstalledStat = async (
  version: string | null = null,
): Promise<void> => {
  const token = await getSessionToken();
  try {
    await axios.get(`/board/installed`, {
      params: { version },
      headers: {
        sessionToken: token?.data,
      },
    });
  } catch (error) {
    console.error(error);
  }
};

const retryUntil = async <T>(
  callback: () => Promise<T | undefined>,
  condition: (response: T | undefined) => boolean,
  maxRetries = 2,
): Promise<T | undefined> => {
  let retryCount = 0;

  while (retryCount <= maxRetries) {
    const response = await callback();

    if (condition(response)) {
      return response;
    }

    if (retryCount < maxRetries) {
      retryCount++;
      await new Promise((resolve) => setTimeout(resolve, 500));
      continue;
    }

    return response;
  }
};

export const getBoardNameAndColumns = async (
  boardId: number,
): Promise<BoardNameAndColumnsResponse | undefined> => {
  return retryUntil(
    () =>
      API(
        `
        query getBoardNameAndColumns($ids: [ID!]) {
          boards(ids: $ids){
            id
            name
            columns {
              id
              title
              type
              settings_str
            }
          }
        }
        `,
        { ids: [boardId] },
      ),
    (response) => {
      const boards = response?.data?.boards;
      return boards != null && boards.length > 0 && boards[0] != null;
    },
  );
};

export const getItemAndBoardDetails = async (
  id: number,
): Promise<ItemAndBoardDetailsResponse | undefined> => {
  return await API(
    `
      query getItemAndBoardDetails($ids: [ID!]) {
        items(ids: $ids) {
          id
          name
          state
          board {
            id
            name
          }
        }
      }
      `,
    { ids: [id] },
  );
};

export const getColumnValues = async (
  id: number,
): Promise<ColumnValuesResponse | undefined> => {
  return retryUntil(
    () =>
      API(
        `
    query getColumnValues($ids: [ID!]) {
      items(ids: $ids) {
        id
        name
        board{
          id
          name
          columns{
              id
              type
              settings_str
              title
          }
        }
        column_values {
          id
          text
          column {
            title
          }
          type
          ... on EmailValue {
            email
          }
          ... on MirrorValue {
            display_value
            mirrored_items {
              linked_board_id
              linked_item { id }
            }
          }
          ... on BoardRelationValue {
            display_value
          }
          ... on PeopleValue {
            persons_and_teams { id kind }
          }
        }
      }
    }
    `,
        { ids: [id] },
      ),
    (response) => {
      const items = response?.data?.items;
      return items != null && items.length > 0 && items[0] != null;
    },
  );
};

export const getEmailColumnValue = async (
  id: number,
  emailColId?: string | string[],
): Promise<EmailColumnValueResponse | undefined> => {
  const options: {
    ids: number[];
    emailColId?: string[];
  } = {
    ids: [id],
  };

  if (emailColId) {
    options.emailColId = Array.isArray(emailColId)
      ? [...emailColId]
      : [emailColId];
  }

  const query = `
  query getEmailColumnValue($ids: [ID!], $emailColId: [String!]) {
    items(ids: $ids) {
      id
      column_values (ids: $emailColId) {
        id
        text
        column{
          title
        }
        type
        value
        ... on EmailValue {
          email
          updated_at
        }
        ... on MirrorValue {
          mirrored_items{
            mirrored_value{
              __typename
            }
          }
          display_value
          id
        }
      }
    }
  }
  `;

  return await API(query, options);
};

export const updateStatusColumn = async ({
  itemId,
  boardId,
  columnValue,
  columnId,
}: {
  itemId: number;
  boardId: number;
  columnValue: string;
  columnId: string;
}): Promise<UpdateStatusColumnResponse | undefined> => {
  const value = JSON.stringify({
    [columnId]: {
      label: columnValue,
    },
  });
  return await API(
    `mutation updateStatusColumn($boardId: ID!, $itemId: ID!, $value: JSON!) {
      change_multiple_column_values(board_id: $boardId, item_id: $itemId, column_values: $value, create_labels_if_missing: true) {
        id
      }
    }`,
    {
      boardId,
      itemId,
      value,
    },
  );
};

export type BoardSubscribersResponse = MondayApiResponse<{
  boards: Array<{
    subscribers: Array<{
      id: string;
      name: string;
      email: string;
    }>;
  }>;
}>;

export const getBoardSubscribers = async (
  boardId: number,
): Promise<BoardSubscribersResponse | undefined> => {
  return await API(
    `query getBoardSubscribers($ids: [ID!]) {
      boards(ids: $ids) {
        subscribers {
          id
          name
          email
        }
      }
    }`,
    { ids: [boardId] },
  );
};

export const createColumn = async ({
  boardId,
  columnType,
  title,
}: {
  boardId: number;
  columnType: string;
  title: string;
}): Promise<CreateColumnResponse | undefined> => {
  return await API(
    `mutation createColumn($boardId: ID!, $columnType: ColumnType!, $title: String!) {
      create_column(board_id: $boardId, title: $title, column_type: $columnType) {
        id
        title
        description
        type
        settings_str
      }
    }`,
    {
      boardId: Number(boardId),
      columnType,
      title,
    },
  );
};
